import { supabase } from "@/integrations/supabase/client";
import { useDeviceFingerprint } from "./useDeviceFingerprint";
import { useIPGeolocation } from "./useIPGeolocation";
import { logger } from "@/lib/logger";

export type SecurityEventType = 
  | "failed_login"
  | "unauthorized_access"
  | "suspicious_activity"
  | "multiple_failed_attempts"
  | "geolocation_mismatch"
  | "device_fingerprint_mismatch"
  | "rate_limit_exceeded"
  | "admin_access_attempt"
  | "role_escalation_attempt"
  | "data_breach_attempt"
  | "sql_injection_attempt"
  | "xss_attempt";

export type SecuritySeverity = "critical" | "high" | "medium" | "low";

interface SecurityEventDetails {
  description: string;
  event_type: SecurityEventType;
  severity?: SecuritySeverity;
  user_id?: string;
  was_blocked?: boolean;
  details?: Record<string, any>;
}

export function useSecurityLogger() {
  const deviceFingerprint = useDeviceFingerprint();
  const { geolocation } = useIPGeolocation();

  const determineSeverity = (eventType: SecurityEventType): SecuritySeverity => {
    const severityMap: Record<SecurityEventType, SecuritySeverity> = {
      failed_login: "low",
      unauthorized_access: "high",
      suspicious_activity: "medium",
      multiple_failed_attempts: "high",
      geolocation_mismatch: "medium",
      device_fingerprint_mismatch: "medium",
      rate_limit_exceeded: "medium",
      admin_access_attempt: "critical",
      role_escalation_attempt: "critical",
      data_breach_attempt: "critical",
      sql_injection_attempt: "critical",
      xss_attempt: "critical",
    };
    return severityMap[eventType] || "medium";
  };

  const logSecurityEvent = async (eventDetails: SecurityEventDetails) => {
    try {
      const severity = eventDetails.severity || determineSeverity(eventDetails.event_type);

      const { error } = await supabase.from("security_events").insert({
        user_id: eventDetails.user_id || null,
        event_type: eventDetails.event_type,
        severity,
        ip_address: null, // Privacy: Don't store IP addresses
        geolocation: geolocation ? {
          country: geolocation.country,
          city: geolocation.city,
          region: geolocation.region,
          timezone: geolocation.timezone,
        } : null,
        device_fingerprint: deviceFingerprint?.fingerprint_hash || null,
        description: eventDetails.description,
        details: {
          ...eventDetails.details,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          device_info: deviceFingerprint?.device_info || null,
          browser: deviceFingerprint?.browser,
          os: deviceFingerprint?.os,
          platform: deviceFingerprint?.platform,
        },
        was_blocked: eventDetails.was_blocked || false,
        resolution_status: "pending",
      });

      if (error) {
        logger.error("Failed to log security event", error);
      }
    } catch (error) {
      logger.error("Security logging error", error);
    }
  };

  return { logSecurityEvent };
}
